import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface HeroStat {
  value: string;
  label: string;
}

interface HeroCTA {
  label: string;
  onClick?: () => void;
  icon?: React.ReactNode;
}

interface HeroSectionProps {
  role: 'physician' | 'non-physician';
  badgeText: string;
  title: string;
  description: string;
  stats?: HeroStat[];
  cta?: HeroCTA;
  subtitle?: string;
  className?: string;
}

const roleConfig = {
  physician: {
    gradient: 'from-eventus-teal/90 via-eventus-navy/95 to-eventus-navy',
    ctaBg: 'bg-eventus-purple hover:bg-eventus-purple-dark',
  },
  'non-physician': {
    gradient: 'from-eventus-teal/90 via-eventus-navy/95 to-eventus-navy',
    ctaBg: 'bg-eventus-purple hover:bg-eventus-purple-dark',
  },
} as const;

export function HeroSection({
  role,
  badgeText,
  title,
  description,
  stats,
  cta,
  subtitle,
  className,
}: HeroSectionProps) {
  const { gradient, ctaBg } = roleConfig[role];

  return (
    <section className={cn(`bg-gradient-to-br ${gradient} pt-20 pb-6 md:pt-24 md:pb-8`, className)}>
      <div className="max-w-6xl mx-auto px-6">
        <Badge className="bg-white/10 text-white border-white/20 mb-4">
          {badgeText}
        </Badge>

        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
          {title}
        </h1>

        <p className="text-lg text-white/90 mb-0">
          {description}
        </p>

        {subtitle && (
          <p className="text-white/70 text-sm mb-6">
            <strong className="text-white/90">For:</strong> {subtitle}
          </p>
        )}

        {stats && stats.length > 0 && (
          <div className="flex flex-wrap gap-4 mb-6">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="px-4 py-2 bg-white/10 rounded-lg border border-white/20"
              >
                <span className="text-xl font-bold text-white">{stat.value}</span>
                <span className="text-sm text-white/80 ml-2">{stat.label}</span>
              </div>
            ))}
          </div>
        )}

        {cta && (
          <button
            onClick={cta.onClick}
            className={cn(
              'inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors',
              ctaBg
            )}
          >
            {cta.icon}
            {cta.label}
          </button>
        )}
      </div>
    </section>
  );
}
